import moment from 'moment';
import { type ComponentProps, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { useLocalizedCalendarDay } from '@treams/treams-hooks';
import { AttendeeProposal, MeetingFragment } from '@treams/treams-network';
import { Button, Card, FlexGap, Separator, UserRow } from '@treams/treams-ui';

type Must<T> = {
	[P in keyof T]-?: NonNullable<T[P]>;
};

type AttendeeWithProposal = Must<Pick<MeetingFragment['attendees'][number], 'user' | 'proposal'>>;

const Container = styled(FlexGap)`
	background-color: ${(props) => props.theme.colors.lightBlueLight};
`;

export function MeetingProposals({
	meeting,
	canUpdate,
	onAccept,
	...rest
}: ComponentProps<typeof FlexGap> & {
	meeting: MeetingFragment;
	canUpdate?: boolean;
	onAccept: (proposal: AttendeeWithProposal) => Promise<unknown>;
}) {
	const newProposals = meeting.attendees.filter(
		(attendee) => !!attendee.proposal && isTimeRangeDifferent(attendee.proposal, meeting),
	) as AttendeeWithProposal[];

	if (newProposals.length === 0) {
		return null;
	}

	return (
		<>
			<Container gap={8} p={3} flexDirection="column" {...rest}>
				{newProposals.map(({ user, proposal }) => (
					<MeetingProposal
						key={user.id}
						meeting={meeting}
						user={user}
						proposal={proposal}
						canUpdate={canUpdate}
						onAccept={() => onAccept({ user, proposal })}
					/>
				))}
			</Container>
			<Separator />
		</>
	);
}

function MeetingProposal(
	props: {
		meeting: MeetingFragment;
		canUpdate?: boolean;
		onAccept: () => Promise<unknown>;
	} & AttendeeWithProposal,
) {
	const { t } = useTranslation();
	const startSameDay = useFormatProposalTime(props.proposal.startsAt);
	const startOther = useLocalizedCalendarDay(props.proposal.startsAt, { includeTime: true });

	const start = useMemo(() => {
		if (moment(props.proposal.startsAt).isSame(moment(props.meeting.startsAt), 'day')) {
			return startSameDay;
		}
		return startOther;
	}, [props.proposal.startsAt, props.meeting.startsAt, startOther, startSameDay]);

	const end = useFormatProposalTime(props.proposal.endsAt);

	return (
		<Card p={2}>
			<UserRow
				user={props.user}
				avatarProps={{ size: 24 }}
				renderUserTitle={() =>
					props.proposal.endsAt
						? t('meeting.proposal.attendeeProposed', {
								name: `${props.user.firstName} ${props.user.lastName}`,
								start,
								end,
							})
						: t('meeting.proposal.attendeeProposedStart', {
								name: `${props.user.firstName} ${props.user.lastName}`,
								start,
							})
				}
			>
				{props.canUpdate && (
					<Button title={t('meeting.proposal.accept')} onClick={props.onAccept} type="card" small />
				)}
			</UserRow>
		</Card>
	);
}

function useFormatProposalTime(date: moment.MomentInput) {
	return useMemo(() => moment(date).format('LT'), [date]);
}

type TimeRange = Pick<AttendeeProposal, 'startsAt' | 'endsAt'>;
function isTimeRangeDifferent(a: TimeRange, b: TimeRange) {
	if (!moment(a.startsAt).isSame(moment(b.startsAt))) {
		return true;
	}

	if (a.endsAt && b.endsAt && !moment(a.endsAt).isSame(moment(b.endsAt))) {
		return true;
	}

	if (a.endsAt && !b.endsAt) {
		return true;
	}

	if (!a.endsAt && b.endsAt) {
		return true;
	}

	return false;
}
